import React, { useEffect, useRef, useState } from 'react';
import TextBrowser from './components/TextBrowser';
import BookCredits from './components/BookCredits';
import Reader from './components/Reader';
import SRSReview from './components/SRSReview';
import SettingsScreen from './components/SettingsScreen';
import BackHeader from './components/BackHeader';
import Login from './components/Login';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useVocab } from './hooks/useVocab';
import { useFontScale } from './hooks/useFontScale';
import { isDue } from './utils/anki';
import { BUILD_ID } from './buildId';
import { startCloudSync } from './utils/cloudSync';
import { startVocabSync } from './utils/vocabSync';
import { STATIC } from './staticMode';
import { loadLibrary, loadStory } from './library';
import { parsePath, pathFor, rowForSlug } from './routes';
import { routeStore } from './staticStore';
import './App.css';

// 關於 credits its books from the SAME record the library card uses, in the
// library's own north→south order, so the two lists can never drift apart. That
// order and those credits now live in the manifest (scripts/library_meta.mjs),
// which is what keeps a new book from needing a deploy — it used to be a table
// here plus a bundled import of all sixteen stories.
const bookList = (lib) => lib.aboutOrder.flatMap(([dialect, name]) =>
  lib.stories.filter(s => s.dialect === dialect).map(t => ({
    dialect, name, title: t.title, credit: t,
  })));

// 小王子 and 會說話的書 are credited in separate paragraphs because only the first
// is the 2015 原民會 series — crediting them from one sentence would put a series
//編輯團隊 on a book it had nothing to do with.
const isSeries = (b) => !!b.credit.series;

// Visible build marker so you can SEE which bundle is loaded (grep marker: BUILDID).
export { BUILD_ID };

// 關於 — ports the phone AboutScreen verbatim.
function About({ onBack, lib }) {
  return (
    <div className="about">
      <BackHeader title="關於" onBack={onBack} />
      <div className="ab-scroll">
        <h3 className="ab-h ab-h0">收錄語言</h3>
        <p className="ab-p">
          族語閱讀器是學習台灣原住民族語的有聲閱讀工具，目前收錄兩本共同讀本，
          十六種族語：賽德克語（南投 Tgdaya 德固達雅）、太魯閣語、泰雅語、
          布農語（郡群 Isbukun）、阿美語、噶瑪蘭語、撒奇萊雅語、排灣語、魯凱語、
          卑南語、賽夏語、鄒語、卡那卡那富語、拉阿魯哇語、達悟語（雅美）與邵語。
          《小王子》十六種族語版本齊全；《會說話的葉子》現有十五種，邵語不在
          此書之列。
        </p>

        <h3 className="ab-h">功能</h3>
        <p className="ab-li">· 四種閱讀層級：片（詞組）、句、段、文（全文）。</p>
        <p className="ab-li">· 真人朗讀，隨音檔逐詞高亮，並顯示波形。</p>
        <p className="ab-li">· 點擊單詞查看字典解釋，標記為學習中或已知。</p>
        <p className="ab-li">· 「學習中」的詞會進入間隔複習。</p>
        <p className="ab-li">· 各層級皆可切換顯示中文翻譯。</p>

        <h3 className="ab-h">譯者與版本來源</h3>
        <p className="ab-p">
          各語版本皆出自原住民族委員會《臺灣原住民族16 族語言學習故事繪本－
          小王子》（2015，原住民族語言研究發展中心執行，總編輯 Yedda Palemeq、
          監製 劉宇陽、責任編輯 阿洛．牡卓兒 Ado moco’），法文原著為安托萬·德·
          聖-埃克蘇佩里。各語版本皆由該版譯者親自朗讀；以下姓名逐本錄自各書版權頁。
        </p>
        {bookList(lib).filter(isSeries).map(({ name, title, credit }) => (
          <p className="ab-li" key={credit.code}>
            · {name}《{title}》：<BookCredits credit={credit} />
            {credit.reviewers && `；文字校訂 ${credit.reviewers}`}
          </p>
        ))}
        {bookList(lib).filter(b => isSeries(b) && b.credit.note).map(({ name, title, credit }) => (
          <p className="ab-p" key={credit.code}>
            {name}《{title}》{credit.note}
          </p>
        ))}
        <p className="ab-p">
          我們寧可從缺，也不把某一本的譯者掛到另一本上；若您知道待查者的姓名，
          懇請來信告知，我們會補上。
        </p>
        {bookList(lib).filter(b => !isSeries(b)).map(({ name, title, credit }) => (
          <p className="ab-li" key={credit.code}>
            · {name}《{title}》：<BookCredits credit={credit} />
          </p>
        ))}
        <p className="ab-p">
          《會說話的葉子》為語發中心 2016 年族語讀本，講述塞闊雅（Sequoyah）為
          切羅基族創造文字的故事；各語版本的翻譯與朗讀者逐本列於上方。
          賽德克語版由郭明正 Dakis Pawan 翻譯並朗讀，原錄音有少數句子受損，
          以其本人的語音模型修復，其餘皆為真人錄音。
        </p>
        <p className="ab-p">
          版本與姓名來源：<a className="ab-mail" href="https://www.ilrdf.org.tw/ilrdc/ebook.ilrdc.tw/book/104/" target="_blank" rel="noreferrer">2015 年《小王子》</a>、
          <a className="ab-mail" href="https://www.ilrdf.org.tw/ilrdc/ebook.ilrdc.tw/book/105/" target="_blank" rel="noreferrer">2016 年族語讀本版權頁</a>。
          邵語《小王子》的公開出版目錄未列此版本，錄音來源、翻譯與朗讀者仍待核實。
        </p>

        <h3 className="ab-h">這是進行中的計畫，我們需要幫忙</h3>
        <p className="ab-p">
          各版本的翻譯與朗讀多出自原住民族語言研究發展中心的族語譯本與公開影片；
          單詞的字典解釋則由程式自動比對族語辭典（財團法人原住民族語言研究發展
          基金會（ILRDF）《原住民族語言線上辭典》e-dictionary.ilrdf.org.tw、Blust
          《南島語比較辭典》）產生，並非逐詞人工校訂。因此各語言的詞彙標註完整度
          差異很大——多數語言已接近全部標出，但少數（如邵語）目前僅約三分之一，
          個別段落的族語與中文也尚未逐句對齊，仍有待校訂。
        </p>
        <p className="ab-p">
          我們堅持「寧可從缺」：與其猜一個可能錯的解釋，不如留白。空白的詞格代表
          目前找不到可靠出處，而不是遺漏。
        </p>
        <p className="ab-p">
          這些都需要族人的幫忙。如果您是族語老師、母語者或語言學研究者，誠摯邀請您
          協助校訂翻譯、逐句對齊、補齊詞彙解釋，尤其是邵語等文獻資源較少的語言。
        </p>

        <h3 className="ab-h">校訂致謝</h3>
        <p className="ab-p">
          感謝 DJ Hatfield 協助校訂阿美語。本書沿用了一九七〇年代教會
          拼寫法的 ^，ceka〔刺〕、mili’epah〔喝酒〕、taengad〔明亮〕、
          kalifoti’an〔該睡的時候〕等詞今日的寫法，都是據此補上的。
        </p>

        <h3 className="ab-h">意見回饋</h3>
        <p className="ab-p">歡迎來信：<a className="ab-mail" href="mailto:shidailun@gmail.com">shidailun@gmail.com</a></p>

        <p className="ab-note">語言保存計畫</p>
      </div>
    </div>
  );
}

// Any runtime crash must be VISIBLE — a silent failure looks like a removed
// feature (word-view "disappearing") and is undiagnosable from a phone.
class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 16, background: '#7f1d1d', color: '#fff', fontSize: 14 }}>
          <b>程式發生錯誤</b>（請截圖回報）：{String(this.state.err && this.state.err.message)}
          <button style={{ marginLeft: 12, padding: '2px 10px' }}
            onClick={() => this.setState({ err: null })}>繼續</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppInner() {
  const { user, ready, logout } = useAuth();
  const { getLearningWords } = useVocab();
  const [fontScale, setFontScale] = useFontScale();
  // Opens straight on the shelf (or on the remembered address — see boot below).
  // There used to be a Welcome screen in front of it — hero, a paragraph, and a
  // 進入書庫 button — which meant every visit cost a tap to get to the only thing
  // anyone comes here for. The hero and the title survive as the library's own
  // banner; the paragraph and the button are gone.
  const [currentText, setCurrentText] = useState(null);
  const [jumpTarget, setJumpTarget] = useState(null);   // {segId, sentId, wIdx, view, focus} — open reader here
  const [readerOrigin, setReaderOrigin] = useState(null);  // {view} — the reader view 跳到複習 launched from
  const [resumeReview, setResumeReview] = useState(false);  // resume the study session
  const [resumeWord, setResumeWord] = useState(null);  // land review on this word (pre-reveal)
  const [resumeDialect, setResumeDialect] = useState(null);  // review scope on resume
  const [lib, setLib] = useState(null);      // the manifest — null while loading, false if it failed
  const [opening, setOpening] = useState(null);   // code of the book being fetched

  // The address we booted at. A bare visit to the domain reopens the last screen
  // (that is the whole "remember where I was" promise); any other path is an
  // explicit request and wins over the memory — otherwise a link someone shared
  // would silently redirect to wherever the recipient happened to be last.
  const bootRef = useRef(null);
  if (bootRef.current === null) {
    const here = window.location.pathname;
    const remembered = here === '/' && !window.location.search ? routeStore.get() : '';
    bootRef.current = (remembered ? parsePath(remembered) : parsePath(here)) || { screen: 'library' };
  }
  const boot = bootRef.current;
  // Reader is never the FIRST screen: the story is a fetch now, so /tao/ lands on
  // the shelf with that card busy for as long as the ~16 kB takes, then switches.
  const [screen, setScreen] = useState(boot.screen === 'reader' ? 'library' : boot.screen);
  // Only a /<slug>/ boot has to wait for the manifest; every other address is
  // already applied by the useState above.
  const bootedRef = useRef(boot.screen !== 'reader');
  const urlSyncedRef = useRef(false);    // first URL write replaces, later ones push

  // The manifest is ~12 kB and every screen that lists a book needs it, so it is
  // fetched once here rather than by each of them. It is now on the critical path
  // for the first paint (the shelf IS the landing screen), which is what `gate`
  // below is for — an unreachable bucket has to say so, not show an empty shelf.
  useEffect(() => { loadLibrary().then(setLib, () => setLib(false)); }, []);

  const due = getLearningWords().filter(isDue).length;   // 複習 pill badge (matches the phone)

  // Opening a book is a network call now (the story is no longer in the bundle),
  // so this waits before switching screens: `screen === 'reader' && currentText`
  // would otherwise render an empty reader for as long as the fetch takes.
  function openText(row) {
    setOpening(row.code);
    loadStory(row).then(
      (story) => {
        setJumpTarget(null);
        setReaderOrigin(null);
        setCurrentText(story);
        setScreen('reader');
        setOpening(null);
      },
      () => setOpening(null),   // stay in the library; the card un-busies and can be retried
    );
  }

  // ── address ────────────────────────────────────────────────────────────────
  // Exactly ONE place writes the URL, and it derives the path from the screen
  // rather than being called at each navigation — a new way into the reader
  // therefore cannot forget to update the address. It writes only when the path
  // actually differs, which is also what makes the popstate handler below safe:
  // by then `location` already IS the new path, so this sees no difference and
  // does not push the entry straight back on.
  useEffect(() => {
    if (!bootedRef.current || opening) return;   // mid-fetch the screen is still the shelf
    const path = pathFor(screen, screen === 'reader' ? currentText : null);
    if (path !== window.location.pathname) {
      // The first write straightens the boot address (a remembered /tao/, or the
      // /yami/ alias) and must NOT become a history entry you can press Back into.
      window.history[urlSyncedRef.current ? 'pushState' : 'replaceState']({}, '', path);
    }
    urlSyncedRef.current = true;
    routeStore.set(path);
  }, [screen, currentText, opening]);

  // Resolve a /<slug>/ boot once the manifest is in — the slug means nothing
  // before then. An unknown slug falls back to the shelf rather than leaving a
  // card busy forever; a manifest that never loads leaves the URL untouched, so
  // a reload after the connection comes back still opens the book that was asked for.
  useEffect(() => {
    if (bootedRef.current || !lib) return;
    const row = rowForSlug(lib, boot.slug, boot.book);
    bootedRef.current = true;
    if (row) openText(row);
  }, [lib, boot]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Back/forward. Re-registered every render on purpose: the handler closes over
  // `lib` and `currentText`, and a stale `lib` here means Back into /tao/ silently
  // does nothing.
  useEffect(() => {
    const onPop = () => {
      const p = parsePath(window.location.pathname) || { screen: 'library' };
      if (p.screen === 'reader') {
        const row = rowForSlug(lib, p.slug, p.book);
        if (row) {
          // Compared on the CODE, not the slug: two books in one language share a
          // slug, so a slug test would make Back out of /tgdaya/leaf/ leave the
          // 會說話的書 on screen under the Little Prince's address.
          if (!currentText || currentText.code !== row.code) openText(row);
          return;
        }
      }
      setJumpTarget(null); setReaderOrigin(null); setCurrentText(null);
      setResumeReview(false); setResumeWord(null); setResumeDialect(null);
      setScreen(p.screen === 'reader' ? 'library' : p.screen);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  });

  // The tab title is the other half of "which language am I in" — it is what a
  // phone shows in the tab switcher and what a bookmark is named after.
  useEffect(() => {
    document.title = screen === 'reader' && currentText
      ? `${currentText.title}｜族語閱讀器` : '族語閱讀器';
  }, [screen, currentText]);

  // 跳到故事 (review → reader). Both directions land in WORD-VIEW (popup) on the word:
  //  review-origin → 片 word-view (segment coords); reader-origin → the view I started in,
  //  word-view on the EXACT word I launched 跳到複習 from (the captured anchor coords).
  function jumpToStory(story, segId, dia, word, wi, sentId) {
    setResumeWord(word || null);
    setResumeDialect(dia || null);
    setCurrentText(story);
    if (readerOrigin) {
      setJumpTarget({ segId, sentId, view: readerOrigin.view, anchorId: readerOrigin.anchorId, anchorWIdx: readerOrigin.anchorWIdx, focus: true });
      setReaderOrigin(null);
    } else {
      setJumpTarget({ segId, sentId, view: 'segment', anchorId: segId, anchorWIdx: wi, focus: true });
    }
    setScreen('reader');
  }

  // 跳到複習 (reader → review): land on THIS word's card, pre-reveal. Remember the origin
  // view AND the exact word coords (item id + word index as rendered) so the return
  // (跳到故事) re-opens word-view on the same word, in that view.
  function jumpToReview(word, dia, originView, anchorId, anchorWIdx) {
    setReaderOrigin(originView ? { view: originView, anchorId, anchorWIdx } : null);
    setResumeDialect(dia || null);
    setResumeWord(word || null); setResumeReview(true);
    setCurrentText(null); setScreen('srs');
  }

  function goBack() {
    setJumpTarget(null);
    setCurrentText(null);
    setReaderOrigin(null);
    setScreen('library');
  }

  if (!ready) return <div className="app-loading">…</div>;
  if (!user) return <Login />;

  // 書庫, 複習 and 關於 all read the manifest; 歡迎 and 設定 do not, so they stay
  // reachable even if the bucket is unreachable. A failed manifest says so rather
  // than showing an empty shelf, which would read as "the books are gone".
  const gate = (node) => (lib ? node
    : <div className="app-loading">{lib === false ? '書庫載入失敗，請重新整理' : '…'}</div>);

  return (
    <div className="app" style={{ '--fs': fontScale }}>
      <main className="app-main">
        {screen === 'library' && (
          <>
            {/* The name lives ON the hero now, in the sky above his hair, so the
                header no longer carries a 族語閱讀器 button — it had nowhere left
                to go once the Welcome screen it returned to stopped existing. */}
            {/* The pills sit INSIDE the hero, on its bottom edge, rather than in
                a band beneath it — the band read as a dark stripe separating the
                artwork from the shelf. Nesting them is what lets them overlay the
                image at all: .lib-hero is the positioned ancestor they anchor to. */}
            <div className="lib-hero">
              <img src={(process.env.PUBLIC_URL || '') + '/welcome-hero.jpg?v=4'} alt="" />
              <h1 className="lib-hero-title">族語閱讀器</h1>
              <header className="ph-header is-lib">
                <nav className="ph-pills">
                  <button className="ph-pill"
                    onClick={() => { setCurrentText(null); setReaderOrigin(null); setResumeReview(false); setResumeWord(null); setResumeDialect(null); setScreen('srs'); }}
                  >複習{due ? ` ${due}` : ''}</button>
                  <button className="ph-pill" onClick={() => setScreen('settings')}>設定</button>
                  <button className="ph-pill" onClick={() => setScreen('about')}>關於</button>
                </nav>
              </header>
            </div>
            {gate(<TextBrowser lib={lib} onSelect={openText} opening={opening} />)}
          </>
        )}
        {screen === 'reader' && currentText && <Reader text={currentText} target={jumpTarget} onBack={goBack} onJumpToReview={jumpToReview} />}
        {screen === 'srs' && gate(
          <SRSReview lib={lib} onBack={goBack} onJump={jumpToStory} resume={resumeReview} resumeWord={resumeWord}
            resumeDialect={resumeDialect} onResumed={() => { setResumeReview(false); setResumeWord(null); }} />
        )}
        {screen === 'about' && gate(<About onBack={goBack} lib={lib} />)}
        {screen === 'settings' && (
          <SettingsScreen onBack={goBack} fontScale={fontScale} setFontScale={setFontScale}
            version={BUILD_ID} user={user} logout={logout} />
        )}
      </main>
      <div className="build-badge" title="loaded bundle build id">{BUILD_ID}</div>
    </div>
  );
}

export default function App() {
  // Static build: progress mirrors to the cloud automatically
  // (utils/cloudSync.js) — a fresh install pulls it back by itself.
  // vocabSync.js runs alongside it: cloudSync is whole-blob backup/restore,
  // vocabSync is a live per-word merge (needed for a second device, or the
  // future review hub, to push a grade back in).
  useEffect(() => {
    if (!STATIC) return undefined;
    const stopSync = startCloudSync();
    const stopVocabSync = startVocabSync();
    return () => { stopSync(); stopVocabSync(); };
  }, []);

  return (
    <AuthProvider>
      <ErrorBoundary>
        <AppInner />
      </ErrorBoundary>
    </AuthProvider>
  );
}
